import { cn } from "@/lib/utils";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

/** Figma: radio-button — size = 24px / 20px / 16px */
export type RadioButtonSize = 24 | 20 | 16;

interface RadioButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  size?: RadioButtonSize;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}

// ============================================
// Figma 디자인 토큰
// ============================================

/** Box 사이즈 (Figma number/24, number/20, number/16) */
const boxSizeStyles: Record<RadioButtonSize, string> = {
  24: "size-6",
  20: "size-5",
  16: "size-4",
};

/** Glyph 사이즈 — Figma 원본은 24px box 안 20px glyph(=83.3%)다. */
const glyphSizeStyles: Record<RadioButtonSize, string> = {
  24: "size-5",
  20: "size-[16.667px]",
  16: "size-[13.333px]",
};

/** Figma 원본 vector(20×20) — 바깥 2px 링. */
const RING_PATH =
  "M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18Z";

/** checked 일 때 링 안쪽에 찍히는 지름 10px 점. */
const DOT_PATH =
  "M10 15C8.67392 15 7.40215 14.4732 6.46447 13.5355C5.52678 12.5979 5 11.3261 5 10C5 8.67392 5.52678 7.40215 6.46447 6.46447C7.40215 5.52678 8.67392 5 10 5C11.3261 5 12.5979 5.52678 13.5355 6.46447C14.4732 7.40215 15 8.67392 15 10C15 11.3261 14.4732 12.5979 13.5355 13.5355C12.5979 14.4732 11.3261 15 10 15Z";

// ============================================
// 컴포넌트
// ============================================

/**
 * Figma `radio-button` 컴포넌트.
 * - unchecked: Label/Normal + opacity 24%
 * - checked: Primary Blue(#2B7FFF) 링 + 점
 * - disabled: opacity 8%
 */
export const RadioButton = forwardRef<HTMLButtonElement, RadioButtonProps>(
  (
    {
      size = 20,
      checked = false,
      disabled = false,
      className,
      onChange,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onChange?.(!checked);
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "inline-flex shrink-0 items-center justify-center focus-visible:outline-none",
          boxSizeStyles[size],
          disabled ? "cursor-not-allowed opacity-8" : "cursor-pointer",
          !disabled && !checked && "opacity-24",
          className,
        )}
        {...props}
      >
        <svg
          className={cn(glyphSizeStyles[size])}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d={RING_PATH}
            className={checked ? "fill-blue-500" : "fill-label-normal"}
          />
          {checked && <path d={DOT_PATH} className="fill-blue-500" />}
        </svg>
      </button>
    );
  },
);

RadioButton.displayName = "RadioButton";

export default RadioButton;
